
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../src/libs/supabase';
import { Reveal } from '../Reveal';
import { LeadFormModal } from '../LeadFormModal';
import {
    Code2, Rocket, Globe, Cpu, ArrowRight,
    Zap, Search, Bot, MapPin, Layout,
    Check, X, MessageSquare, ShieldCheck, BarChart, CheckCircle2, Server, Loader2, Smartphone, Eye, MousePointer2, Stars
} from 'lucide-react';
import { Page } from '../../types';

// --- VISUAL: 3D HERO BROWSER ---
const HeroVisual = () => {
    const { t } = useTranslation();
    return (
        <div className="relative w-full max-w-2xl mx-auto h-[350px] md:h-[450px] perspective-1000 group">
            {/* Floating Elements Background */}
            <div className="absolute top-10 -right-10 w-24 h-24 bg-clever-teal/10 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-10 -left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-pulse delay-700"></div>

            {/* Abstract Browser Window */}
            <div className="absolute inset-0 bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-gray-200 transform rotate-x-6 rotate-y-[-6deg] transition-transform duration-700 group-hover:rotate-x-0 group-hover:rotate-y-0 overflow-hidden hover:shadow-[0_30px_60px_rgba(0,0,0,0.25)]">
                {/* Browser Bar */}
                <div className="h-10 bg-gray-50 border-b border-gray-100 flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <div className="ml-4 h-6 bg-white border border-gray-200 rounded-md w-2/3 flex items-center px-3 text-[10px] text-gray-400 font-mono shadow-sm">
                        {t('shared.website.visuals.browser.url')}
                    </div>
                </div>

                {/* Content Building Animation */}
                <div className="p-8 relative h-full overflow-hidden">
                    {/* Grid Lines */}
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:20px_20px]"></div>

                    {/* Header Block */}
                    <div className="h-16 w-2/3 bg-gray-900 rounded-lg mb-8 animate-[slideUp_0.8s_ease-out_forwards] opacity-0 relative z-10">
                        <div className="absolute -right-4 -top-4 bg-clever-teal text-white text-[10px] px-2 py-1 rounded font-bold animate-bounce">H1</div>
                    </div>

                    {/* Hero Grid */}
                    <div className="grid grid-cols-2 gap-8 relative z-10">
                        <div className="space-y-4">
                            <div className="h-4 w-full bg-gray-200 rounded animate-[slideUp_0.8s_ease-out_0.2s_forwards] opacity-0"></div>
                            <div className="h-4 w-3/4 bg-gray-200 rounded animate-[slideUp_0.8s_ease-out_0.3s_forwards] opacity-0"></div>
                            <div className="h-4 w-5/6 bg-gray-200 rounded animate-[slideUp_0.8s_ease-out_0.4s_forwards] opacity-0"></div>

                            <div className="flex gap-3 mt-6">
                                <div className="h-10 w-24 bg-[#0B1121] rounded shadow-lg transform transition-transform hover:scale-105 animate-[slideUp_0.8s_ease-out_0.6s_forwards] opacity-0"></div>
                                <div className="h-10 w-24 border-2 border-gray-200 rounded transform transition-transform hover:scale-105 animate-[slideUp_0.8s_ease-out_0.7s_forwards] opacity-0"></div>
                            </div>
                        </div>

                        <div className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl border border-gray-200 flex items-center justify-center relative overflow-hidden animate-[scaleIn_0.8s_ease-out_0.5s_forwards] opacity-0 shadow-inner">
                            <Rocket size={48} className="text-gray-400 animate-pulse" />
                            {/* Code Overlay */}
                            <div className="absolute bottom-2 right-2 font-mono text-[8px] text-gray-400 bg-white/80 px-2 py-1 rounded border border-gray-200">
                                &lt;img src="..." /&gt;
                            </div>
                        </div>
                    </div>
                </div>

                {/* Floating Code Overlay - The "Engine" */}
                <div className="hidden md:block absolute -right-5 bottom-10 bg-[#0B1121]/95 backdrop-blur-md p-5 rounded-lg shadow-2xl border border-gray-700 font-mono text-[10px] text-blue-300 w-56 transform translate-z-20 rotate-[-3deg] group-hover:rotate-0 transition-all duration-500 hover:scale-105 cursor-default">
                    <div className="flex justify-between border-b border-gray-700 pb-2 mb-2">
                        <span className="text-gray-500">server.tsx</span>
                        <span className="text-green-400">● 2ms</span>
                    </div>
                    <div><span className="text-purple-400">export default</span> <span className="text-yellow-300">async</span> <span className="text-blue-400">function</span> Page() {'{'}</div>
                    <div className="pl-4 text-gray-400">// Server-Side Rendered</div>
                    <div className="pl-4"><span className="text-purple-400">const</span> data = <span className="text-purple-400">await</span> db.get();</div>
                    <div className="pl-4"><span className="text-purple-400">return</span> (</div>
                    <div className="pl-6 text-emerald-300">&lt;SpeedLayer data={'{data}'} /&gt;</div>
                    <div className="pl-4">);</div>
                    <div>{'}'}</div>
                </div>
            </div>

            <style>{`
            @keyframes slideUp {
                0% { transform: translateY(20px); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
            }
            @keyframes scaleIn {
                0% { transform: scale(0.9); opacity: 0; }
                100% { transform: scale(1); opacity: 1; }
            }
        `}</style>
        </div>
    )
}

// --- VISUAL: SPEED COMPARISON ---
const SpeedVisual = () => {
    const { t } = useTranslation();
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(prev => (prev + 1) % 100);
        }, 50);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative h-auto bg-white rounded-xl border border-gray-200 p-8 overflow-hidden flex flex-col gap-6 shadow-lg">

            {/* Header */}
            <div className="flex justify-between items-end border-b border-gray-100 pb-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">{t('shared.website.visuals.speed.benchmark')}</div>
                    <h4 className="text-xl font-bold text-gray-900">{t('shared.website.visuals.speed.title')}</h4>
                </div>
                <div className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-500">{t('shared.website.visuals.speed.vitals')}</div>
            </div>

            {/* Standard Lane */}
            <div className="relative">
                <div className="flex justify-between text-xs font-bold uppercase tracking-widest mb-2 text-gray-400">
                    <span>{t('shared.website.visuals.speed.standard')}</span>
                    <span>3.8s</span>
                </div>
                <div className="w-full h-8 bg-gray-100 rounded overflow-hidden relative">
                    {/* Slow moving bar */}
                    <div className="absolute top-0 left-0 h-full bg-gray-300 w-[35%] flex items-center justify-end px-2">
                        <Loader2 size={14} className="text-gray-500 animate-spin" />
                    </div>
                </div>
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <X size={12} className="text-red-500" /> {t('shared.website.problem.speed.badge')}
                </div>
            </div>

            {/* CleverDigital Lane */}
            <div className="relative">
                <div className="flex justify-between text-xs font-bold uppercase tracking-widest mb-2 text-[#0B1121]">
                    <span>{t('shared.website.visuals.speed.clever')}</span>
                    <span className="text-green-600">0.2s</span>
                </div>
                <div className="w-full h-8 bg-gray-100 rounded overflow-hidden relative border border-green-100">
                    {/* Fast moving bar */}
                    <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#0B1121] to-blue-600 w-[98%] flex items-center justify-end px-2 shadow-lg shadow-blue-500/20">
                        <Zap size={14} className="text-white fill-yellow-400" />
                    </div>
                </div>
                <div className="text-xs text-green-600 mt-1 flex items-center gap-1 font-bold">
                    <Check size={12} /> {t('shared.website.visuals.speed.favorite')}
                </div>
            </div>
        </div>
    )
}

// --- VISUAL: LLM STRUCTURE ---
const LLMDataVisual = () => {
    const { t } = useTranslation();
    return (
        <div className="bg-[#0B1121] p-6 md:p-8 rounded-xl border border-indigo-500/30 shadow-2xl font-mono text-xs text-gray-400 relative overflow-hidden group hover:border-indigo-500/50 transition-colors">
            {/* Header Console */}
            <div className="flex justify-between items-center border-b border-gray-800 pb-4 mb-4">
                <div className="flex items-center gap-2">
                    <Bot size={16} className="text-indigo-400" />
                    <span className="text-indigo-200 font-bold">AI_CRAWLER_V4</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-500 text-[10px] uppercase tracking-wider">{t('shared.website.visuals.llm.connected')}</span>
                </div>
            </div>

            <div className="space-y-2 relative z-10">
                <div className="flex gap-4 opacity-50">
                    <span className="text-gray-600 select-none">01</span>
                    <span>{t('shared.website.visuals.llm.scan')}</span>
                </div>
                <div className="flex gap-4">
                    <span className="text-gray-600 select-none">02</span>
                    <span className="text-purple-400">{"<script type=\"application/ld+json\">"}</span>
                </div>
                <div className="flex gap-4 group-hover:bg-indigo-900/10 -mx-4 px-4 transition-colors">
                    <span className="text-gray-600 select-none">03</span>
                    <span className="pl-4"><span className="text-blue-400">"@context"</span>: <span className="text-emerald-400">"https://schema.org"</span>,</span>
                </div>
                <div className="flex gap-4 group-hover:bg-indigo-900/10 -mx-4 px-4 transition-colors">
                    <span className="text-gray-600 select-none">04</span>
                    <span className="pl-4"><span className="text-blue-400">"@type"</span>: <span className="text-emerald-400">"ProfessionalService"</span>,</span>
                </div>
                <div className="flex gap-4 bg-indigo-900/20 -mx-6 px-6 py-1 border-l-2 border-indigo-500">
                    <span className="text-indigo-500 select-none">05</span>
                    <span className="pl-4"><span className="text-blue-400">"name"</span>: <span className="text-white font-bold">"{t('shared.website.visuals.llm.company_name')}"</span>,</span>
                </div>
                <div className="flex gap-4">
                    <span className="text-gray-600 select-none">06</span>
                    <span className="pl-4"><span className="text-blue-400">"description"</span>: <span className="text-emerald-400">"{t('shared.website.visuals.llm.market_leader')}"</span></span>
                </div>
                <div className="flex gap-4">
                    <span className="text-gray-600 select-none">07</span>
                    <span className="text-purple-400">{"</script>"}</span>
                </div>
                <div className="flex gap-4 opacity-50">
                    <span className="text-gray-600 select-none">08</span>
                    <span className="text-green-400">✓ {t('shared.website.visuals.llm.indexed')}</span>
                </div>
            </div>

            {/* AI Badge Overlay */}
            <div className="absolute bottom-4 right-4 bg-indigo-600 text-white px-3 py-1 rounded-full text-[10px] font-bold shadow-lg flex items-center gap-2">
                <Eye size={12} />
                <span>{t('shared.website.visuals.llm.readable')}</span>
            </div>
        </div>
    )
}

interface WebsitePageProps {
    onNavigate?: (page: Page) => void;
}

interface ContactForm {
    firstName: string;
    lastName: string;
    company: string;
    phone: string;
    email: string;
    note: string;
}

export const WebsitePage: React.FC<WebsitePageProps> = ({ onNavigate }) => {
    const { t, i18n } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">

            {/* Hero Section */}
            <section className="relative pt-24 pb-20 overflow-hidden">
                <div className="container px-4 mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="lg:w-1/2">
                            <Reveal>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-clever-teal/10 border border-clever-teal/20 text-clever-teal text-xs font-bold mb-6">
                                    <Code2 size={14} />
                                    <span>{t('shared.website.hero.badge')}</span>
                                </div>
                                <h1 className="text-6xl md:text-8xl font-black text-gray-900 leading-none mb-6">
                                    {t('shared.website.hero.title_line1')}<br />
                                    <span className="text-clever-teal">{t('shared.website.hero.title_line2')}</span>
                                </h1>
                                <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-xl">
                                    {t('shared.website.hero.subtitle')}
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="px-8 py-4 bg-[#0B1121] text-white rounded-lg font-bold flex items-center gap-2 hover:bg-black transition-all transform hover:scale-105 active:scale-95 shadow-xl"
                                    >
                                        {t('shared.website.hero.cta_analysis')} <ArrowRight size={20} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            const element = document.getElementById('problem-section');
                                            element?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-900 rounded-lg font-bold hover:bg-gray-50 transition-all"
                                    >
                                        {t('shared.website.hero.cta_check')}
                                    </button>
                                </div>
                            </Reveal>
                        </div>
                        <div className="lg:w-1/2 w-full">
                            <Reveal delay={0.2}>
                                <HeroVisual />
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Problem (Speed & AI) */}
            <section id="problem-section" className="py-24 bg-gray-50">
                <div className="container px-4 mx-auto">
                    <Reveal>
                        <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-16 max-w-2xl">
                            {t('shared.website.problem.title_line1')}<br />
                            <span className="text-gray-400">{t('shared.website.problem.title_line2')}</span>
                        </h2>
                    </Reveal>

                    <div className="grid lg:grid-cols-2 gap-12">
                        <Reveal delay={0.1}>
                            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 flex flex-col h-full">
                                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center text-red-600 mb-8">
                                    <Zap size={30} />
                                </div>
                                <h3 className="text-2xl font-bold mb-4">{t('shared.website.problem.speed.title')}</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    {t('shared.website.problem.speed.desc')}
                                </p>
                                <div className="mt-auto">
                                    <SpeedVisual />
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 flex flex-col h-full">
                                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-8">
                                    <Search size={30} />
                                </div>
                                <h3 className="text-2xl font-bold mb-4">{t('shared.website.problem.ai.title')}</h3>
                                <p className="text-gray-600 leading-relaxed mb-8">
                                    {t('shared.website.problem.ai.desc')}
                                </p>
                                <div className="mt-auto">
                                    <LLMDataVisual />
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* The Solution (Tech Stack) */}
            <section className="py-32 bg-white relative overflow-hidden">
                <div className="container px-4 mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <Reveal>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-bold mb-6">
                                <ShieldCheck size={14} />
                                <span>{t('shared.website.solution.badge')}</span>
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">{t('shared.website.solution.title')}</h2>
                            <p className="text-xl text-gray-600">
                                {t('shared.website.solution.intro')}
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Globe className="text-clever-teal" />,
                                title: t('shared.website.solution.features.react.title'),
                                desc: t('shared.website.solution.features.react.desc')
                            },
                            {
                                icon: <Layout className="text-clever-teal" />,
                                title: t('shared.website.solution.features.cms.title'),
                                desc: t('shared.website.solution.features.cms.desc')
                            },
                            {
                                icon: <Server className="text-clever-teal" />,
                                title: t('shared.website.solution.features.ssr.title'),
                                desc: t('shared.website.solution.features.ssr.desc')
                            }
                        ].map((feature, idx) => (
                            <Reveal key={idx} delay={idx * 0.1}>
                                <div className="p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-clever-teal/30 transition-all group active:scale-[0.98]">
                                    <div className="mb-6 transform group-hover:scale-110 transition-transform">{feature.icon}</div>
                                    <h4 className="text-xl font-bold mb-4">{feature.title}</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI / ChatGTP Ready */}
            <section className="py-24 bg-[#0B1121] text-white">
                <div className="container px-4 mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="lg:w-1/2">
                            <Reveal>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold mb-6">
                                    <Bot size={14} />
                                    <span>{t('shared.website.ai_readiness.badge')}</span>
                                </div>
                                <h2 className="text-4xl md:text-6xl font-black mb-8">
                                    {t('shared.website.ai_readiness.title_line1')}<br />
                                    <span className="text-indigo-400">{t('shared.website.ai_readiness.title_line2')}</span>
                                </h2>
                                <p className="text-xl text-gray-400 mb-6 leading-relaxed">
                                    {t('shared.website.ai_readiness.intro')}
                                </p>
                                <p className="text-lg text-gray-500 mb-8">
                                    {t('shared.website.ai_readiness.intro2')}
                                </p>
                                <ul className="space-y-4">
                                    {[
                                        t('shared.website.ai_readiness.items.0'),
                                        t('shared.website.ai_readiness.items.1')
                                    ].map((item, idx) => (
                                        <li key={idx} className="flex items-center gap-3 text-indigo-200">
                                            <div className="w-5 h-5 rounded-full bg-indigo-500/20 flex items-center justify-center">
                                                <Check size={12} />
                                            </div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        </div>
                        <div className="lg:w-1/2 w-full">
                            <Reveal delay={0.2}>
                                <div className="relative">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl blur opacity-30"></div>
                                    <LLMDataVisual />
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. PRO TIP (Google Maps Hack) */}
            <section className="py-32 bg-gray-50">
                <div className="max-w-4xl mx-auto px-6">
                    <Reveal>
                        <div className="bg-white border-l-4 border-clever-teal p-12 shadow-2xl relative overflow-hidden rounded-r-xl">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-clever-teal/5 rounded-full blur-[80px] pointer-events-none"></div>

                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-12 h-12 bg-clever-teal text-white flex items-center justify-center rounded-full shadow-lg">
                                    <MapPin size={24} />
                                </div>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">{t('shared.website.pro_tip.badge')}</div>
                                    <div className="text-lg font-bold text-gray-900">{t('shared.website.pro_tip.label')}</div>
                                </div>
                            </div>

                            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">{t('shared.website.pro_tip.title')}</h3>

                            <div className="bg-clever-teal text-white p-8 rounded-2xl shadow-2xl transform rotate-1 relative z-10">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold mb-6">
                                    <Stars size={14} />
                                    <span>{t('shared.website.pro_tip.badge')}</span>
                                </div>
                                <div className="text-xs font-bold uppercase tracking-widest text-white/60 mb-2">{t('shared.website.pro_tip.label')}</div>
                                <h4 className="text-2xl font-black mb-4 leading-tight">{t('shared.website.pro_tip.title')}</h4>
                                <p className="text-white/90 leading-relaxed">
                                    {t('shared.website.pro_tip.text1')}<br /><br />
                                    {t('shared.website.pro_tip.text2')} <strong>{t('shared.website.pro_tip.text3')}</strong>
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gray-50 border-y border-gray-100">
                <div className="container px-4 mx-auto text-center">
                    <Reveal>
                        <h2 className="text-5xl md:text-7xl font-black mb-8">
                            {t('shared.website.cta.title_line1')}<br />
                            <span className="text-clever-teal uppercase">{t('shared.website.cta.title_line2')}</span>
                        </h2>
                        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                            {t('shared.website.cta.text')}
                        </p>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-12 py-5 bg-[#0B1121] text-white rounded-xl font-bold text-lg hover:bg-black transition-all transform hover:scale-105 active:scale-95 shadow-2xl flex items-center gap-3 mx-auto"
                        >
                            {t('shared.website.cta.button')} <Rocket size={24} />
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT MODAL */}
            <LeadFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sector="Website Audit"
            />
        </div>
    );
};
